import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Storage } from '@ionic/storage-angular';
import { Drivers } from '@ionic/storage';
import { capTask } from '@capacitor-community/sqlite';
import { OfflineDatabaseService } from '../offline/offline-database.service';
import { HttpsService } from './https.service';
import { ImageCacheService } from './image-cache.service';
import { AuthorizationContext, AuthorizationService } from './authorization.service';

interface ObraCacheAccessScope {
  userId: number;
  role: string;
  adminUserId: number;
  clientId: number;
  permissionsRevision: number;
  accessExpiresAt: number | null;
}

interface ObraLocalEnvelope {
  data: any[];
  savedAt: number;
  version: number;
  cursor: string;
  serverUpdatedAt: string | null;
  accessScope: ObraCacheAccessScope;
}

interface ObraCacheOptions {
  forceRefresh?: boolean;
  onCached?: (obras: any[]) => void;
}

interface ObraDetalheOptions {
  forceRefresh?: boolean;
  onCached?: (obra: any) => void;
}

type NativeObraScopeStatus = 'ausente' | 'compativel' | 'incompativel';

export interface ProjectQuotaAvailability {
  allowed: boolean;
  used: number;
  limit: number;
  remaining: number;
}

const OBRA_MYSQL_COLUMNS = [
  'projeto_id', 'imagem_obra', 'user_email', 'user_id', 'id_cliente',
  'cliente_nome', 'projeto_nome', 'projeto_nome_slug', 'cep_obra',
  'logradouro_obra', 'number_obra', 'complemento_obra', 'bairro_obra',
  'cidade_obra', 'estado_obra', 'projeto_proprietario', 'projeto_cidade',
  'projeto_endereco', 'projeto_desc', 'relatorio_fotografico_descricao',
  'tipo_obra', 'status_obra', 'data_prevista_inicio', 'data_prevista_entrega',
  'valor_contratado', 'responsavel_obra', 'projeto_date_create', 'api_construfacil'
] as const;

const OBRA_LOCAL_COLUMNS = [
  ...OBRA_MYSQL_COLUMNS,
  'local_account_user_id', 'permissoes_obra_json', 'imagem_obra_cache',
  'imagem_obra_url', 'imagem_obra_servidor', 'imagem_obra_origem', 'local_updated_at'
] as const;

@Injectable({ providedIn: 'root' })
export class ObraRepositoryService {
  private storage: Storage | null = null;
  private initialized = false;
  private storageInitialization: Promise<void> | null = null;
  private readonly version = 15;
  private readonly checkIntervalMs = 5 * 60 * 1000;
  private readonly retryIntervalMs = 60 * 1000;
  private readonly pendingSync = new Map<string, Promise<any[]>>();
  private readonly lastCheck = new Map<string, number>();
  private readonly nextRetry = new Map<string, number>();

  constructor(
    private https: HttpsService,
    private imageCacheService: ImageCacheService,
    private authorization: AuthorizationService,
    private database: OfflineDatabaseService,
  ) {}

  async initializeForUser(userId: any): Promise<any[]> {
    return this.getObras(userId);
  }

  async getObras(userId: any, options: ObraCacheOptions = {}): Promise<any[]> {
    const normalizedUserId = this.normalizeUserId(userId);
    if (!normalizedUserId) return [];
    const context = await this.authorization.load();
    const cached = await this.readLocal(normalizedUserId, context);
    const hasSnapshot = await this.hasLocalSnapshot(normalizedUserId, context);
    if (hasSnapshot) options.onCached?.(cached);

    try {
      return await this.synchronize(normalizedUserId, context, !!options.forceRefresh, false);
    } catch (error) {
      if (hasSnapshot) return cached;
      throw error;
    }
  }

  async refreshObras(userId: any): Promise<any[]> {
    const normalizedUserId = this.normalizeUserId(userId);
    if (!normalizedUserId) return [];
    const context = await this.authorization.refresh(true);
    return this.synchronize(normalizedUserId, context, true, true);
  }

  async getObraDetalhe(userId: any, obraId: any, options: ObraDetalheOptions = {}): Promise<any | null> {
    const normalizedUserId = this.normalizeUserId(userId);
    if (!normalizedUserId) return null;
    const context = await this.authorization.load();
    const cached = (await this.readLocal(normalizedUserId, context))
      .find((obra) => this.getObraId(obra) == obraId) || null;
    if (cached) options.onCached?.(cached);

    try {
      const obras = await this.getObras(normalizedUserId, { forceRefresh: !!options.forceRefresh });
      return obras.find((obra) => this.getObraId(obra) == obraId) || cached;
    } catch (error) {
      if (cached) return cached;
      throw error;
    }
  }

  async getProjectQuota(): Promise<ProjectQuotaAvailability> {
    const formData = new FormData();
    formData.append('data', JSON.stringify({ callback: 'read_project_quota' }));
    const response: any = await this.https.post(formData, 'obra.php');
    if (!response?.success) throw new Error(response?.msg || 'Não foi possível consultar o limite de obras.');
    return {
      allowed: response.allowed === true,
      used: Math.max(0, Number(response.used || 0)),
      limit: Math.max(0, Number(response.limit || 0)),
      remaining: Math.max(0, Number(response.remaining || 0)),
    };
  }

  async upsertObra(userId: any, obra: any): Promise<void> {
    const normalizedUserId = this.normalizeUserId(userId);
    const context = await this.authorization.load();
    const obras = await this.readLocal(normalizedUserId, context);
    const id = this.getObraId(obra);
    if (!id) {
      await this.invalidateObras(normalizedUserId);
      return;
    }
    const next = obras.some((item) => this.getObraId(item) == id)
      ? obras.map((item) => this.getObraId(item) == id ? { ...item, ...obra } : item)
      : [obra, ...obras];
    await this.replaceLocal(
      normalizedUserId,
      next,
      await this.getLocalCursor(normalizedUserId, context),
      null,
      context,
    );
  }

  async removeObraFromCache(userId: any, obraId: any): Promise<void> {
    const normalizedUserId = this.normalizeUserId(userId);
    const context = await this.authorization.load();
    const obras = await this.readLocal(normalizedUserId, context);
    await this.replaceLocal(
      normalizedUserId,
      obras.filter((item) => this.getObraId(item) != obraId),
      await this.getLocalCursor(normalizedUserId, context),
      null,
      context,
    );
  }

  async invalidateObras(userId: any): Promise<void> {
    await this.initStorage();
    const normalizedUserId = this.normalizeUserId(userId);
    const prefix = `${normalizedUserId}:`;
    [...this.lastCheck.keys()].filter((key) => key.startsWith(prefix)).forEach((key) => this.lastCheck.delete(key));
    if (this.canUseNativeDatabase(normalizedUserId)) {
      await this.purgeNativeSnapshot(normalizedUserId);
    } else {
      const cached = await this.storage?.get(this.cacheKey(normalizedUserId));
      await this.purgeWebSnapshot(normalizedUserId, Array.isArray(cached?.data) ? cached.data : []);
    }
  }

  private synchronize(
    userId: number,
    context: AuthorizationContext,
    forceCheck: boolean,
    forceFull: boolean,
  ): Promise<any[]> {
    const scopeKey = this.scopeKey(userId, context);
    const current = this.pendingSync.get(scopeKey);
    if (current) return current;
    const now = Date.now();
    if (!forceCheck && now < (this.nextRetry.get(scopeKey) || 0)) return this.readLocal(userId, context);
    if (!forceCheck && now - (this.lastCheck.get(scopeKey) || 0) < this.checkIntervalMs) {
      return this.readLocal(userId, context);
    }

    const operation = this.performSynchronization(userId, context, forceFull)
      .then((obras) => {
        this.lastCheck.set(scopeKey, Date.now());
        this.nextRetry.delete(scopeKey);
        return obras;
      })
      .catch((error) => {
        this.nextRetry.set(scopeKey, Date.now() + this.retryIntervalMs);
        throw error;
      })
      .finally(() => this.pendingSync.delete(scopeKey));
    this.pendingSync.set(scopeKey, operation);
    return operation;
  }

  private async performSynchronization(
    userId: number,
    context: AuthorizationContext,
    forceFull: boolean,
  ): Promise<any[]> {
    await this.initStorage();
    if (Capacitor.isNativePlatform() && (!this.database.isReady || this.database.userId !== userId)) {
      await this.database.initializeForUser(userId);
    }
    const cursor = forceFull ? '' : await this.getLocalCursor(userId, context);
    const formData = new FormData();
    formData.append('data', JSON.stringify({ callback: 'sync_obras', cursor }));
    const response: any = await this.https.post(formData, 'obra-sync.php');
    if (!response?.success) throw new Error(response?.msg || 'Não foi possível sincronizar as obras.');

    const latestContext = this.authorization.getContext();
    if (!latestContext || Number(latestContext.userId) !== userId) return [];
    if (!this.isCacheCompatible(this.createAccessScope(context), latestContext)) {
      // Uma resposta iniciada com permissões antigas nunca pode repor o snapshot revogado.
      return this.readLocal(userId, latestContext);
    }

    if (response.changed || forceFull) {
      const autorizadas = this.filterAuthorizedWorks(Array.isArray(response.result) ? response.result : [], context);
      const comImagens = await this.cacheImagensObras(autorizadas);
      await this.replaceLocal(
        userId,
        comImagens,
        String(response.cursor ?? '0'),
        response.updated_at || null,
        context,
      );
    } else {
      await this.updateLocalCursor(userId, String(response.cursor ?? cursor ?? '0'), response.updated_at || null, context);
    }
    return this.readLocal(userId, context);
  }

  private async readLocal(userId: number, context: AuthorizationContext): Promise<any[]> {
    await this.initStorage();
    if (this.canUseNativeDatabase(userId)) {
      const scopeStatus = await this.getNativeScopeStatus(context);
      if (scopeStatus === 'incompativel') {
        await this.purgeNativeSnapshot(userId);
        return [];
      }
      if (scopeStatus === 'ausente') return this.readLegacyData(userId, context);
      const rows = await this.database.query<any>(
        'SELECT * FROM obras WHERE local_account_user_id = ? ORDER BY projeto_id DESC',
        [userId],
      );
      return this.filterAuthorizedWorks(rows.map((row) => this.mapNativeRow(row)), context);
    }
    const envelope = await this.getWebEnvelope(userId, context);
    return this.filterAuthorizedWorks(envelope?.data || [], context);
  }

  private async replaceLocal(
    userId: number,
    obras: any[],
    cursor: string,
    serverUpdatedAt: string | null,
    context: AuthorizationContext,
  ): Promise<void> {
    await this.initStorage();
    const normalized = this.sortObras(obras || []);
    if (this.canUseNativeDatabase(userId)) {
      const now = new Date().toISOString();
      const placeholders = OBRA_LOCAL_COLUMNS.map(() => '?').join(', ');
      const tasks: capTask[] = [{ statement: 'DELETE FROM obras WHERE local_account_user_id = ?', values: [userId] }];
      normalized.forEach((obra) => tasks.push({
        statement: `INSERT INTO obras (${OBRA_LOCAL_COLUMNS.join(', ')}) VALUES (${placeholders})`,
        values: OBRA_LOCAL_COLUMNS.map((column) => this.nativeValue(column, obra, userId, now)),
      }));
      tasks.push({
        statement: `INSERT INTO sync_state (scope, cursor, last_pulled_at, updated_at)
          VALUES ('obras', ?, ?, ?)
          ON CONFLICT(scope) DO UPDATE SET cursor = excluded.cursor,
            last_pulled_at = excluded.last_pulled_at, updated_at = excluded.updated_at`,
        values: [cursor || '0', serverUpdatedAt || now, now],
      });
      tasks.push({
        statement: `INSERT INTO app_metadata (key, value, updated_at)
          VALUES ('obras_access_scope', ?, ?)
          ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
        values: [JSON.stringify(this.createAccessScope(context)), now],
      });
      await this.database.executeTransaction(tasks);
      return;
    }

    const envelope: ObraLocalEnvelope = {
      data: normalized.map((obra) => this.prepareObraForStorage(obra)),
      savedAt: Date.now(),
      version: this.version,
      cursor: cursor || '0',
      serverUpdatedAt,
      accessScope: this.createAccessScope(context),
    };
    await this.storage?.set(this.cacheKey(userId), envelope);
  }

  private async updateLocalCursor(
    userId: number,
    cursor: string,
    serverUpdatedAt: string | null,
    context: AuthorizationContext,
  ): Promise<void> {
    if (this.canUseNativeDatabase(userId)) {
      const now = new Date().toISOString();
      await this.database.executeTransaction([
        {
          statement: `INSERT INTO sync_state (scope, cursor, last_pulled_at, updated_at)
            VALUES ('obras', ?, ?, ?)
            ON CONFLICT(scope) DO UPDATE SET cursor = excluded.cursor,
              last_pulled_at = excluded.last_pulled_at, updated_at = excluded.updated_at`,
          values: [cursor || '0', serverUpdatedAt || now, now],
        },
        {
          statement: `INSERT INTO app_metadata (key, value, updated_at)
            VALUES ('obras_access_scope', ?, ?)
            ON CONFLICT(key) DO UPDATE SET value = excluded.value, updated_at = excluded.updated_at`,
          values: [JSON.stringify(this.createAccessScope(context)), now],
        },
      ]);
      return;
    }
    const envelope = await this.getWebEnvelope(userId, context);
    if (!envelope) return;
    await this.storage?.set(this.cacheKey(userId), {
      ...envelope,
      cursor: cursor || '0',
      serverUpdatedAt,
      savedAt: Date.now(),
    });
  }

  private async getLocalCursor(userId: number, context: AuthorizationContext): Promise<string> {
    if (this.canUseNativeDatabase(userId)) {
      const scopeStatus = await this.getNativeScopeStatus(context);
      if (scopeStatus === 'incompativel') await this.purgeNativeSnapshot(userId);
      if (scopeStatus !== 'compativel') return '';
      const rows = await this.database.query<{ cursor: string | null }>(
        'SELECT cursor FROM sync_state WHERE scope = ? LIMIT 1',
        ['obras'],
      );
      return String(rows[0]?.cursor || '');
    }
    return String((await this.getWebEnvelope(userId, context))?.cursor || '');
  }

  private async hasLocalSnapshot(userId: number, context: AuthorizationContext): Promise<boolean> {
    if (this.canUseNativeDatabase(userId)) {
      const scopeStatus = await this.getNativeScopeStatus(context);
      if (scopeStatus === 'incompativel') {
        await this.purgeNativeSnapshot(userId);
        return false;
      }
      if (scopeStatus === 'ausente') return !!(await this.readLegacyData(userId, context)).length;
      const rows = await this.database.query<{ scope: string }>(
        'SELECT scope FROM sync_state WHERE scope = ? LIMIT 1',
        ['obras'],
      );
      return rows.length > 0 || !!(await this.readLegacyData(userId, context)).length;
    }
    return !!await this.getWebEnvelope(userId, context);
  }

  private async initStorage(): Promise<void> {
    if (this.initialized) return;
    if (!this.storageInitialization) this.storageInitialization = this.createStorage();
    await this.storageInitialization;
  }

  private async createStorage(): Promise<void> {
    const storage = new Storage({ name: 'construfacil', storeName: 'app_cache', driverOrder: [Drivers.IndexedDB] });
    this.storage = await storage.create();
    this.initialized = true;
  }

  private async getWebEnvelope(userId: number, context: AuthorizationContext): Promise<ObraLocalEnvelope | null> {
    await this.initStorage();
    const cached = await this.storage?.get(this.cacheKey(userId));
    if (!cached) {
      return null;
    }
    if (!Array.isArray(cached.data) || !cached.accessScope || !this.isCacheCompatible(cached.accessScope, context)) {
      await this.purgeWebSnapshot(userId, Array.isArray(cached.data) ? cached.data : []);
      return null;
    }
    if (cached.version === this.version) return cached as ObraLocalEnvelope;
    const migrated: ObraLocalEnvelope = {
      data: cached.data,
      savedAt: Number(cached.savedAt || Date.now()),
      version: this.version,
      cursor: '',
      serverUpdatedAt: null,
      accessScope: cached.accessScope,
    };
    await this.storage?.set(this.cacheKey(userId), migrated);
    return migrated;
  }

  private async readLegacyData(userId: number, context: AuthorizationContext): Promise<any[]> {
    await this.initStorage();
    const cached = await this.storage?.get(this.cacheKey(userId));
    if (!cached || !Array.isArray(cached.data) || !cached.accessScope || !this.isCacheCompatible(cached.accessScope, context)) return [];
    return this.filterAuthorizedWorks(cached.data, context);
  }

  private async getNativeScopeStatus(context: AuthorizationContext): Promise<NativeObraScopeStatus> {
    const rows = await this.database.query<{ value: string | null }>(
      'SELECT value FROM app_metadata WHERE key = ? LIMIT 1',
      ['obras_access_scope'],
    );
    if (!rows.length || !rows[0].value) return 'ausente';
    try {
      return this.isCacheCompatible(JSON.parse(rows[0].value), context) ? 'compativel' : 'incompativel';
    } catch {
      return 'incompativel';
    }
  }

  private async purgeNativeSnapshot(userId: number): Promise<void> {
    const obras = await this.database.query<{ projeto_id: number }>(
      'SELECT projeto_id FROM obras WHERE local_account_user_id = ?',
      [userId],
    );
    await this.database.executeTransaction([
      { statement: 'DELETE FROM obras WHERE local_account_user_id = ?', values: [userId] },
      { statement: 'DELETE FROM sync_state WHERE scope = ?', values: ['obras'] },
      { statement: 'DELETE FROM app_metadata WHERE key = ?', values: ['obras_access_scope'] },
    ]);
    await this.storage?.remove(this.cacheKey(userId));
    await this.removeCachedWorkImages(obras);
  }

  private async purgeWebSnapshot(userId: number, obras: any[]): Promise<void> {
    await this.storage?.remove(this.cacheKey(userId));
    await this.removeCachedWorkImages(obras);
  }

  private async removeCachedWorkImages(obras: any[]): Promise<void> {
    const ids = [...new Set((obras || []).map((obra) => Number(this.getObraId(obra))).filter((id) => id > 0))];
    await Promise.all(ids.map((obraId) => this.imageCacheService.delete(`obra-${obraId}-capa`).catch(() => false)));
  }

  private createAccessScope(context: AuthorizationContext): ObraCacheAccessScope {
    return {
      userId: Number(context.userId || 0),
      role: context.role,
      adminUserId: Number(context.adminUserId || 0),
      clientId: Number(context.clientId || 0),
      permissionsRevision: Number(context.permissionsRevision || 1),
      accessExpiresAt: context.accessExpiresAt,
    };
  }

  private isCacheCompatible(scope: ObraCacheAccessScope, context: AuthorizationContext): boolean {
    return Number(scope.userId) === Number(context.userId)
      && scope.role === context.role
      && Number(scope.adminUserId) === Number(context.adminUserId)
      && Number(scope.clientId) === Number(context.clientId)
      && Number(scope.permissionsRevision) === Number(context.permissionsRevision)
      && (scope.accessExpiresAt ?? null) === context.accessExpiresAt;
  }

  private filterAuthorizedWorks(obras: any[], context: AuthorizationContext): any[] {
    if (context.role === 'administrador') return obras || [];
    if (context.accessExpiresAt !== null && context.accessExpiresAt < Date.now()) return [];
    if (context.role === 'cliente') return obras || [];
    const appPermissions = new Set((context.appPermissions || []).map((permission) => String(permission).toLowerCase()));
    if (appPermissions.has('*') || appPermissions.has('obras.visualizar') || appPermissions.has('obra.visualizar')) {
      return obras || [];
    }
    const allowedIds = new Set(Object.keys(context.workPermissions || {}).map((id) => Number(id)));
    return (obras || []).filter((obra) => allowedIds.has(Number(this.getObraId(obra))));
  }

  private mapNativeRow(row: any): any {
    let permissions: string[] = [];
    try {
      permissions = JSON.parse(String(row.permissoes_obra_json || '[]'));
    } catch {
      permissions = [];
    }
    return { ...row, obra_id: row.projeto_id, cliente_id: row.id_cliente, permissoes_obra: permissions };
  }

  private nativeValue(column: string, obra: any, userId: number, now: string): any {
    if (column === 'local_account_user_id') return userId;
    if (column === 'permissoes_obra_json') return JSON.stringify(obra?.permissoes_obra || []);
    if (column === 'local_updated_at') return now;
    if (['projeto_id', 'user_id', 'id_cliente'].includes(column)) return obra?.[column] == null ? null : Number(obra[column]);
    return obra?.[column] ?? null;
  }

  private prepareObraForStorage(obra: any): any {
    if (Capacitor.isNativePlatform() || !String(obra?.imagem_obra || '').startsWith('blob:')) return obra;
    return { ...obra, imagem_obra: '', imagem_obra_cache: '', imagem_obra_origem: 'servidor' };
  }

  private canUseNativeDatabase(userId: number): boolean {
    return Capacitor.isNativePlatform() && this.database.isReady && this.database.userId === userId;
  }

  private cacheKey(userId: number): string {
    return `cache:user:${userId}:obras`;
  }

  private scopeKey(userId: number, context: AuthorizationContext): string {
    return `${userId}:${JSON.stringify(this.createAccessScope(context))}`;
  }

  private normalizeUserId(userId: any): number {
    return Math.max(0, Math.trunc(Number(userId || 0)));
  }

  private getObraId(obra: any): any {
    return obra?.projeto_id || obra?.obra_id || null;
  }

  private async cacheImagensObras(obras: any[]): Promise<any[]> {
    const result = [];
    for (const obra of obras || []) result.push(await this.cacheImagemObra(obra));
    return result;
  }

  private async cacheImagemObra(obra: any): Promise<any> {
    const imagemServidor = this.getImagemObraServidor(obra);
    const imagemLocalAtual = this.getImagemObraLocal(obra);
    if (!imagemServidor && !imagemLocalAtual) return obra;

    let imagemLocalValidada = imagemLocalAtual;
    const obraId = this.getObraId(obra);
    if (imagemLocalAtual.startsWith('blob:') && !Capacitor.isNativePlatform() && obraId) {
      const registro = await this.imageCacheService.getByKey(`obra-${obraId}-capa`);
      imagemLocalValidada = registro?.localUrl || '';
    }
    if (imagemLocalValidada) {
      return {
        ...obra,
        imagem_obra: imagemLocalValidada,
        imagem_obra_cache: imagemLocalValidada,
        imagem_obra_url: imagemServidor || obra?.imagem_obra_url || '',
        imagem_obra_servidor: imagemServidor || obra?.imagem_obra_servidor || '',
        imagem_obra_origem: 'cache',
      };
    }

    let imagemCache = '';
    try {
      const registro = await this.imageCacheService.create({
        url: imagemServidor,
        title: obra?.projeto_nome_slug || obra?.projeto_nome || `obra-${obraId || 'capa'}`,
        key: obraId ? `obra-${obraId}-capa` : undefined,
      });
      imagemCache = registro?.localUrl || '';
    } catch {
      imagemCache = '';
    }
    const temImagemLocal = this.isLocalCachedImage(imagemCache);
    return {
      ...obra,
      imagem_obra: temImagemLocal ? imagemCache : imagemServidor,
      imagem_obra_cache: temImagemLocal ? imagemCache : '',
      imagem_obra_url: imagemServidor,
      imagem_obra_servidor: imagemServidor,
      imagem_obra_origem: temImagemLocal ? 'cache' : 'servidor',
    };
  }

  private getImagemObraServidor(obra: any): string {
    const fontes = [obra?.imagem_obra_url, obra?.imagem_obra_servidor, obra?.imagem_obra_original, obra?.imagem_obra];
    return String(fontes.find((src) => src && !this.isLocalCachedImage(src)) || '').trim();
  }

  private getImagemObraLocal(obra: any): string {
    const fontes = [obra?.imagem_obra_cache, obra?.imagem_obra_local, obra?.imagem_obra];
    return String(fontes.find((src) => this.isLocalCachedImage(src)) || '').trim();
  }

  private isLocalCachedImage(src: string): boolean {
    return typeof src === 'string' && (
      src.startsWith('data:image/') || src.startsWith('blob:')
      || src.startsWith('capacitor://') || src.startsWith('file://')
    );
  }

  private sortObras(obras: any[]): any[] {
    return [...(obras || [])].sort((a, b) => Number(this.getObraId(b) || 0) - Number(this.getObraId(a) || 0));
  }
}
